import type { RouteRecordRaw } from 'vue-router'

import {
  BriefcaseIcon,
  CalculatorIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CubeIcon,
  HomeIcon,
  MegaphoneIcon,
  ShoppingCartIcon,
  SquaresPlusIcon,
  TagIcon,
  TvIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/vue/24/outline'

import type { MainSidebarSection } from '@/types/internal/mainSidebarTypes'

import { TEST_IDS_EXTERNAL_VIEWS } from '@/_testIds/testIdsExternalViews.ts'
import { routeAnalyticsHome } from '@/routers/routes/mainRoutes/route2WorkAI/analyticsRoutes.ts'
import { routeAccountingDashboardHome } from '@/routers/routes/mainRoutes/routeAccountings/accountingDashboardRoutes'
import {
  routeAccountingReportsBalanceSheet,
  routeAccountingReportsDatevExport,
  routeAccountingReportsManagementReport,
  routeAccountingReportsProfitAndLoss,
  routeAccountingReportsVatAdvanceReturn,
} from '@/routers/routes/mainRoutes/routeAccountings/accountingReportsRoutes.ts'
import { routeAutoRepeatsHome } from '@/routers/routes/mainRoutes/routeAccountings/autoRepeatsRoutes.ts'
import { routeDunningsHome } from '@/routers/routes/mainRoutes/routeAccountings/dunningRoutes.ts'
import { routeEInvoiceImportsHome } from '@/routers/routes/mainRoutes/routeAccountings/eInvoiceImportsRoutes'
import { routePaymentEntriesHome } from '@/routers/routes/mainRoutes/routeAccountings/paymentEntriesRoutes.ts'
import { routePurchaseInvoicesHome } from '@/routers/routes/mainRoutes/routeAccountings/puchaseInvoiceRoutes'
import { routeSalesInvoicesHome } from '@/routers/routes/mainRoutes/routeAccountings/salesInvoiceRoutes.ts'
import { routeActivityRadarHome } from '@/routers/routes/mainRoutes/routeActivities/activitiesRoutes.ts'
import { routeCalendarHome } from '@/routers/routes/mainRoutes/routeActivities/calendarRoutes.ts'
import {
  routeInboxHome,
  routeOutboxHome,
} from '@/routers/routes/mainRoutes/routeActivities/communicationRoutes'
import { routeTodosHome } from '@/routers/routes/mainRoutes/routeActivities/todoRoutes.ts'
import { routeBuyingDashboardHome } from '@/routers/routes/mainRoutes/routeBuying/buyingDashboardRoutes'
import { routePurchaseOrdersHome } from '@/routers/routes/mainRoutes/routeBuying/purchaseOrderRoutes'
import { routeRequestForQuotationsHome } from '@/routers/routes/mainRoutes/routeBuying/requestForQuotationRoutes'
import { routeSupplierQuotationsHome } from '@/routers/routes/mainRoutes/routeBuying/supplierQuotationRoutes'
import { routeHome } from '@/routers/routes/mainRoutes/routeHome/homeRoutes'
import { routeEmployeesHome } from '@/routers/routes/mainRoutes/routeHR/employeeRoutes.ts'
import { routeMarketingCampaignsHome } from '@/routers/routes/mainRoutes/routeMarketing/marketingCampaignRoutes'
import { routeMarketingDashboardHome } from '@/routers/routes/mainRoutes/routeMarketing/marketingDashboardRoutes'
import { routeDistributionListsHome } from '@/routers/routes/mainRoutes/routeMarketing/routeNewsletter/distributionListRoutes'
import { routeNewsletterArticlesHome } from '@/routers/routes/mainRoutes/routeMarketing/routeNewsletter/newsletterArticleRoutes'
import { routeSubscribersHome } from '@/routers/routes/mainRoutes/routeMarketing/routeNewsletter/subscriberRoutes'
import { routeDeliveryNotesHome } from '@/routers/routes/mainRoutes/routeProjects/deliveryNoteRoutes.ts'
import { routeProjectsHome } from '@/routers/routes/mainRoutes/routeProjects/projectRoutes.ts'
import { routeTasksHome } from '@/routers/routes/mainRoutes/routeProjects/taskRoutes.ts'
import { routeTimesheetsHome } from '@/routers/routes/mainRoutes/routeProjects/timesheetRoutes.ts'
import { routeOrganizationsHome } from '@/routers/routes/mainRoutes/routeRelationships/organizationsRoutes.ts'
import { routePersonsHome } from '@/routers/routes/mainRoutes/routeRelationships/personsRoutes.ts'
import { routeDealsHome } from '@/routers/routes/mainRoutes/routeSales/dealsRoutes.ts'
import { routeQuotationsHome } from '@/routers/routes/mainRoutes/routeSales/quotationsRoutes.ts'
import { routeSalesDashboardHome } from '@/routers/routes/mainRoutes/routeSales/salesDashboardRoutes.ts'
import { routeSalesOrdersHome } from '@/routers/routes/mainRoutes/routeSales/salesOrdersRoutes.ts'
import { routeActivityTypesHome } from '@/routers/routes/mainRoutes/routeServices/activityTypesRoutes.ts'
import { routeItemGroupsHome } from '@/routers/routes/mainRoutes/routeServices/routeItems/itemGroupsRoutes'
import { routeItemsHome } from '@/routers/routes/mainRoutes/routeServices/routeItems/itemsRoutes'
import { routeSerialNosHome } from '@/routers/routes/mainRoutes/routeServices/routeItems/serialNoRoutes'
import { routePricesHome } from '@/routers/routes/mainRoutes/routeServices/routePrices'
import { routeItemPricesHome } from '@/routers/routes/mainRoutes/routeServices/routePrices/itemPricesRoutes.ts'
import { routePriceListsHome } from '@/routers/routes/mainRoutes/routeServices/routePrices/priceListRoutes.ts'
import { routePricingRulesHome } from '@/routers/routes/mainRoutes/routeServices/routePrices/pricingRuleRoutes.ts'
import { routeUOMHome } from '@/routers/routes/mainRoutes/routeServices/uomRoutes.ts'
import { routeCurrencySettingsHome } from '@/routers/routes/mainRoutes/routeSettings/currencyRoutes.ts'
import { routeDataImportHome } from '@/routers/routes/mainRoutes/routeSettings/dataImportRoutes.ts'
import { routeLetterheadHome } from '@/routers/routes/mainRoutes/routeSettings/letterheadRoutes.ts'
import { routeNamingSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/namingSettingsRoutes.ts'
import { routePrintDesignerHome } from '@/routers/routes/mainRoutes/routeSettings/printDesignerRoutes.ts'
import { routePrintFormatsHome } from '@/routers/routes/mainRoutes/routeSettings/printFormatRoutes.ts'
import { routeAccountingSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting'
import { routeAccountsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/accountsRoutes.ts'
import { routeCostCenterHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/costCenterRoutes.ts'
import { routeDatevMasterDataHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/datevMasterDataRoutes.ts'
import { routeDatevSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/datevSettingsRoutes.ts'
import { routeDunningPlanHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/dunningPlanRoutes.ts'
import { routeDunningTypesHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/dunningTypesRoutes.ts'
import { routeManagementReportRowsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/managementReportRowRoutes.ts'
import { routeManagementReportSummaryRowsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/managementReportSummaryRowRoutes.ts'
import { routePaymentTermsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/paymentTermsRoutes.ts'
import { routePaymentTermsTemplatesHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/paymentTermsTemplatesRoutes.ts'
import { routeVatReturnRowsHome } from '@/routers/routes/mainRoutes/routeSettings/routeAccounting/vatReturnRowRoutes.ts'
import { routeAutomationHome } from '@/routers/routes/mainRoutes/routeSettings/routeAutomation'
import { routeSlackHome } from '@/routers/routes/mainRoutes/routeSettings/routeAutomation/slackRoutes.ts'
import { routeBRMSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM'
import { routeCustomerGroupSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/customerGroupRoutes'
import { routePhoneTypeSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/phoneTypeRoutes.ts'
import { routeRatingSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/ratingRoutes.ts'
import { routeRoleSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/roleRoutes.ts'
import { routeSalutationSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/salutationRoutes'
import { routeSupplierGroupSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/supplierGroupRoutes'
import { routeWeblinkTypeSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeBRM/weblinkTypeRoutes.ts'
import { routeEmailSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail'
import { routeConnectedAppHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail/connectedAppRoutes.ts'
import { routeEmailAccountHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail/emailAccountRoutes.ts'
import { routeEmailConfigurationHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail/emailConfigurationRoutes.ts'
import { routeEmailDomainHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail/emailDomainRoutes.ts'
import { routeEmailTemplateHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail/emailTemplateRoutes.ts'
import { routeTokenCacheHome } from '@/routers/routes/mainRoutes/routeSettings/routeEmail/tokenCacheRoutes.ts'
import { routeGoogleCalendarHome } from '@/routers/routes/mainRoutes/routeSettings/routeGoogle/googleCalendarRoutes.ts'
import { routeGoogleSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeGoogle/googleSettingsRoutes.ts'
import { routeSalesSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeSales'
import { routeBuyingCenterRoleSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/routeSales/buyingCenterRoleRoutes'
import { routeQuotationLostReasonHome } from '@/routers/routes/mainRoutes/routeSettings/routeSales/quotationLostReasonRoutes.ts'
import { routeTagHome } from '@/routers/routes/mainRoutes/routeSettings/tagsRoutes.ts'
import { routeTermsAndConditionsSettingsHome } from '@/routers/routes/mainRoutes/routeSettings/termsAndConditionsSettingRoutes.ts'
import { routeUsersHome } from '@/routers/routes/mainRoutes/routeSettings/usersRoutes.ts'
import { routePurchaseReceiptsHome } from '@/routers/routes/mainRoutes/routeStock/purchaseReceiptsRoutes'
import { routeStockBalanceHome } from '@/routers/routes/mainRoutes/routeStock/stockBalanceRoutes'
import { routeStockEntriesHome } from '@/routers/routes/mainRoutes/routeStock/stockEntriesRoutes'
import { routeWarehousesHome } from '@/routers/routes/mainRoutes/routeStock/warehousesRoutes'
import { useSideNavStore } from '@/stores/sideNavStore.ts'

const staticLink = (route: RouteRecordRaw) => {
  return (): RouteRecordRaw => route
}

/**
 * This MainSidebarMap represents the application's page navigation structure.
 * It is organized hierarchically into:
 * - Sections: Main groupings of navigation items
 * - Main menu items: Primary navigation categories
 * - Submenu items: Secondary navigation options under main items
 * - Subroutes: Additional routes associated with menu items
 *
 * The order of items in this map directly reflects how they will be
 * displayed to users in the navigation interface.
 */
/* eslint-disable perfectionist/sort-objects -- Navigation order is intentional */
const MainSidebarMap: MainSidebarSection[] = [
  {
    home: {
      groupIcon: HomeIcon,
      groupLink: staticLink(routeHome),
      groupName: 'sideNav.home',
      menuItems: [
        {
          link: staticLink(routeHome),
          text: 'sideNav.home',
        },
      ],
      singleEntry: true,
    },
  },
  {
    relations: {
      groupIcon: UserGroupIcon,
      groupName: 'sideNav.relations',
      menuItems: [
        {
          link: staticLink(routeOrganizationsHome),
          text: 'sideNav.relationsItemOrganization',
        },
        {
          link: staticLink(routePersonsHome),
          text: 'sideNav.relationsItemPerson',
        },
      ],
      onboardingKey: 'relations',
      settingsLabel: 'settings.brm.title',
      settingsLink: staticLink(routeBRMSettingsHome),
      settingsSubRoutes: [
        routeRoleSettingsHome,
        routePhoneTypeSettingsHome,
        routeSalutationSettingsHome,
        routeRatingSettingsHome,
        routeWeblinkTypeSettingsHome,
        routeCustomerGroupSettingsHome,
        routeSupplierGroupSettingsHome,
      ],
    },
    activities: {
      groupIcon: SquaresPlusIcon,
      groupName: 'sideNav.activities',
      menuItems: [
        {
          link: staticLink(routeActivityRadarHome),
          text: 'sideNav.activities',
        },
        {
          link: staticLink(routeCalendarHome),
          text: 'sideNav.calendar',
        },
        {
          link: staticLink(routeTodosHome),
          text: 'sideNav.myTasks',
        },
        {
          link: (): RouteRecordRaw => {
            return routeInboxHome
          },
          text: 'sideNav.communication',
          visible: (): boolean => {
            const sideNavStore = useSideNavStore()
            return sideNavStore.hasEmailInboxes
          },
        },
        {
          link: (): RouteRecordRaw => {
            return routeOutboxHome
          },
          text: 'sideNav.outbox',
          visible: (): boolean => {
            const sideNavStore = useSideNavStore()
            return sideNavStore.hasEmailInboxes
          },
        },
      ],
      onboardingKey: 'activities',
    },
  },
  {
    sales: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.salesTab,
      groupIcon: BriefcaseIcon,
      groupLink: staticLink(routeSalesDashboardHome),
      groupName: 'sideNav.sales',
      menuItems: [
        {
          link: staticLink(routeDealsHome),
          text: 'sideNav.salesItemDeal',
        },
        {
          link: staticLink(routeQuotationsHome),
          text: 'sideNav.salesItemQuotation',
        },
        {
          link: staticLink(routeSalesOrdersHome),
          text: 'sideNav.salesItemOrder',
        },
      ],
      onboardingKey: 'sales',
      settingsLabel: 'settings.sales.title',
      settingsLink: staticLink(routeSalesSettingsHome),
      settingsSubRoutes: [routeQuotationLostReasonHome, routeBuyingCenterRoleSettingsHome],
    },
    buying: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.buyingTab,
      groupIcon: ShoppingCartIcon,
      groupLink: staticLink(routeBuyingDashboardHome),
      groupName: 'sideNav.buying',
      menuItems: [
        {
          link: staticLink(routeRequestForQuotationsHome),
          text: 'sideNav.buyingRequestForQuotation',
        },
        {
          link: staticLink(routeSupplierQuotationsHome),
          text: 'sideNav.buyingSupplierQuotation',
        },
        {
          link: staticLink(routePurchaseOrdersHome),
          text: 'sideNav.buyingPurchaseOrder',
        },
      ],
      onboardingKey: 'buying',
    },
    project: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.projectTab,
      groupIcon: ClipboardDocumentListIcon,
      groupName: 'sideNav.project',
      menuItems: [
        {
          link: staticLink(routeProjectsHome),
          text: 'sideNav.projectItemProject',
        },
        {
          link: staticLink(routeTasksHome),
          text: 'sideNav.projectItemTask',
        },
        {
          link: staticLink(routeTimesheetsHome),
          text: 'sideNav.projectItemTimesheet',
        },
        {
          link: staticLink(routeDeliveryNotesHome),
          text: 'sideNav.projectItemDeliveryNote',
        },
      ],
      onboardingKey: 'project',
    },
    accounting: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.accountingsTab,
      groupIcon: CalculatorIcon,
      groupName: 'sideNav.accounting',
      groupLink: staticLink(routeAccountingDashboardHome),
      menuItems: [
        {
          link: staticLink(routeSalesInvoicesHome),
          text: 'sideNav.accountingItemSalesInvoice',
        },
        {
          link: staticLink(routePurchaseInvoicesHome),
          subRoutes: [routePurchaseInvoicesHome, routeEInvoiceImportsHome],
          text: 'sideNav.accountingItemPurchaseInvoice',
        },
        {
          link: staticLink(routePaymentEntriesHome),
          text: 'sideNav.accountingItemPaymentEntry',
        },
        {
          link: staticLink(routeAutoRepeatsHome),
          text: 'sideNav.accountingItemAutoRepeat',
        },
        {
          link: staticLink(routeDunningsHome),
          text: 'sideNav.accountingItemDunning',
        },
        {
          link: staticLink(routeAccountingReportsBalanceSheet),
          subRoutes: [
            routeAccountingReportsBalanceSheet,
            routeAccountingReportsProfitAndLoss,
            routeAccountingReportsDatevExport,
            routeAccountingReportsManagementReport,
            routeAccountingReportsVatAdvanceReturn,
          ],
          text: 'sideNav.accountingItemReports',
        },
      ],
      onboardingKey: 'accounting',
      settingsLabel: 'settings.accounting.title',
      settingsLink: staticLink(routeAccountingSettingsHome),
      settingsSubRoutes: [
        routePaymentTermsHome,
        routePaymentTermsTemplatesHome,
        routeDunningTypesHome,
        routeDunningPlanHome,
        routeAccountsHome,
        routeCostCenterHome,
        routeCurrencySettingsHome,
        routeDatevMasterDataHome,
        routeManagementReportRowsHome,
        routeManagementReportSummaryRowsHome,
        routeVatReturnRowsHome,
      ],
    },
  },
  {
    humanResources: {
      beta: true,
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.hrTab,
      groupIcon: UserIcon,
      groupName: 'sideNav.humanResources',
      menuItems: [
        {
          link: staticLink(routeEmployeesHome),
          text: 'sideNav.hrItemEmployee',
        },
      ],
      onboardingKey: 'humanResources',
    },
    services: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.servicesTab,
      groupIcon: TagIcon,
      groupName: 'sideNav.services',
      menuItems: [
        {
          link: staticLink(routeItemsHome),
          subRoutes: [routeItemsHome, routeItemGroupsHome, routeSerialNosHome],
          text: 'sideNav.servicesItemItem',
        },
        {
          link: staticLink(routePricesHome),
          subRoutes: [routeItemPricesHome, routePriceListsHome, routePricingRulesHome],
          text: 'sideNav.prices',
        },
        {
          link: staticLink(routeActivityTypesHome),
          text: 'sideNav.projectItemActivityType',
        },
        {
          link: staticLink(routeUOMHome),
          text: 'sideNav.UOM',
        },
      ],
      onboardingKey: 'services',
    },
    stock: {
      beta: true,
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.stockTab,
      groupIcon: CubeIcon,
      groupName: 'sideNav.stock',
      menuItems: [
        {
          link: staticLink(routeStockBalanceHome),
          text: 'sideNav.stockBalance',
        },
        {
          link: staticLink(routeWarehousesHome),
          text: 'sideNav.stockWarehouse',
        },
        {
          link: staticLink(routePurchaseReceiptsHome),
          text: 'sideNav.stockPurchaseReceipt',
        },
        {
          link: staticLink(routeStockEntriesHome),
          text: 'sideNav.stockStockEntry',
        },
      ],
      onboardingKey: 'stock',
    },
    marketing: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.marketingTab,
      groupIcon: MegaphoneIcon,
      groupName: 'sideNav.marketing',
      menuItems: [
        {
          // TODO: Erstmal ausgeklammert, bis wir die aktuellen Berechtigungsfehler dort gelöst haben
          link: staticLink(routeMarketingDashboardHome),
          text: 'sideNav.marketingDashboard',
        },
        {
          link: staticLink(routeNewsletterArticlesHome),
          subRoutes: [
            routeNewsletterArticlesHome,
            routeDistributionListsHome,
            routeSubscribersHome,
          ],
          text: 'sideNav.marketingNewsletter',
        },
        {
          link: staticLink(routeMarketingCampaignsHome),
          text: 'sideNav.marketingCampaign',
        },
      ],
      onboardingKey: 'marketing',
    },
  },
  {
    settings: {
      dataTestId: TEST_IDS_EXTERNAL_VIEWS.sidebar.settingsTab,
      groupIcon: Cog6ToothIcon,
      groupName: 'sideNav.settings',
      menuItems: [
        {
          link: staticLink(routeTermsAndConditionsSettingsHome),
          text: 'sideNav.termsAndConditions',
        },
        {
          link: staticLink(routeAutomationHome),
          text: 'sideNav.automation',
        },
        {
          link: staticLink(routeNamingSettingsHome),
          text: 'sideNav.namingSettings',
        },
        {
          link: staticLink(routeUsersHome),
          text: 'sideNav.users',
        },
        {
          link: staticLink(routeDataImportHome),
          text: 'sideNav.dataImport',
        },
        {
          link: staticLink(routeLetterheadHome),
          text: 'sideNav.letterhead',
        },
        {
          link: staticLink(routePrintFormatsHome),
          subRoutes: [routePrintDesignerHome],
          text: 'sideNav.printFormat',
        },
        {
          link: staticLink(routeEmailSettingsHome),
          subRoutes: [
            routeEmailConfigurationHome,
            routeConnectedAppHome,
            routeTokenCacheHome,
            routeEmailAccountHome,
            routeEmailDomainHome,
            routeEmailTemplateHome,
          ],
          text: 'sideNav.emailSettings',
        },
        {
          link: staticLink(routeDatevSettingsHome),
          subRoutes: [
            routeDatevSettingsHome,
            routeGoogleSettingsHome,
            routeGoogleCalendarHome,
            routeSlackHome,
          ],
          text: 'sideNav.interfaces',
        },
        {
          link: staticLink(routeTagHome),
          text: 'sideNav.tags',
        },
      ],
      onboardingKey: 'settings',
    },
    preview: {
      groupIcon: TvIcon,
      groupName: 'sideNav.preview',
      menuItems: [
        {
          link: staticLink(routeAnalyticsHome),
          text: 'sideNav.2workAI',
        },
      ],
    },
  },
] as const

export default MainSidebarMap
